// The re-test link's "come back to the report" leg (utils/retestTarget.ts /
// utils/retestLinkView.ts). Same-tab navigation (app.ts, Paul's fix for the
// old target="_blank" link) leaves the reporter/reviewer standing on the
// destination page with no way back to the report's verdict controls unless
// something reopens them — this module carries the report's id there.
//
// Shape mirrors legacy-client.ts's ?lib=<id> handoff exactly: a one-time id
// riding the URL, read + always stripped (matched or not) rather than
// persisted anywhere, so it never lingers in the address bar or ends up in
// a bookmark/shared link.
//
// Deliberately just URL bookkeeping — no api.js import, no report lookup.
// cos-feedback-panel.focusReport() does that lookup and decides "open
// nothing" for a report that doesn't resolve (deleted, or not visible to
// the signed-in caller); this module only ever hands back a raw,
// syntactically-valid id for that caller to try.

export const REPORT_URL_PARAM = "retest_report";

/** Cheap, synchronous pre-check — mirrors legacy-client.ts's
 *  hasPendingLibraryParam so a caller (cos-app-shell's connectedCallback)
 *  can skip any work on the overwhelming majority of boots that never carry
 *  this param. */
export function hasPendingReportParam(): boolean {
	return new URLSearchParams(window.location.search).has(REPORT_URL_PARAM);
}

/**
 * Reads `?retest_report=<id>` off the current URL and always strips it via
 * history.replaceState — matched or not, so a malformed value never lingers
 * any more than a good one would (mirrors legacy-client.ts's
 * applyLibraryFromURL "strip either way" contract).
 *
 * Returns the parsed id, or null when the param was absent or not a
 * positive integer. Does NOT check whether the report actually exists or is
 * visible to the caller — see cos-feedback-panel.focusReport for that.
 */
export function consumeReportFocusParam(): number | null {
	const url = new URL(window.location.href);
	const raw = url.searchParams.get(REPORT_URL_PARAM);
	if (raw === null) return null;
	url.searchParams.delete(REPORT_URL_PARAM);
	history.replaceState(null, "", url.pathname + url.search + url.hash);
	const id = Number(raw);
	return Number.isInteger(id) && id > 0 ? id : null;
}
